<template>
	<section
		v-if="items.length"
		class="consult-service"
		:class="{ 'consult-service--area': isAreaTeacher }"
	>
		<h3 v-if="isAreaTeacher" class="section-title">
			{{ t(`${I18N_NS}.sections.consultService`) }}
		</h3>
		<ul class="consult-service__grid" role="list">
			<li
				v-for="(item, index) in items"
				:key="item.id"
				:ref="el => bindServiceImpression(el, item, index)"
				class="consult-service-card"
				role="listitem"
				@click="toServiceDetail(item, index)"
			>
				<div v-if="isAreaTeacher" class="consult-service-card__image-wrap">
					<img
						class="consult-service-card__image"
						:src="item.cover || defaultCover"
						:alt="item.name"
					/>
				</div>

				<div class="consult-service-card__content">
					<header>
						<div class="consult-service-card__name">{{ item.name }}</div>
					</header>
					<p class="consult-service-card__detail" v-text="item.detail"></p>

					<p v-if="item.discount_tag" class="consult-service-card__original">
						{{ t(`${I18N_NS}.originalPrice`, { price: item.price }) }}
					</p>

					<footer class="consult-service-card__footer">
						<div class="consult-service-card__price">
							<img src="~/assets/images/pc/coin.png" :alt="t(`${I18N_NS}.coinAlt`)" />
							<span>{{ item.final_price }}</span>
							<em v-if="item.discount_tag">{{ t(`${I18N_NS}.finalPrice`) }}</em>
						</div>

						<button
							type="button"
							class="consult-service-card__btn"
							:class="{ 'consult-service-card__btn--icon': isAreaTeacher }"
							:aria-label="t(`${I18N_NS}.orderNow`)"
							@click.stop="order(item, index)"
						>
							<span
								v-if="isAreaTeacher"
								class="consult-service-card__plus"
								aria-hidden="true"
							></span>
							<template v-else>{{ t(`${I18N_NS}.orderNow`) }}</template>
						</button>
					</footer>
				</div>
			</li>
		</ul>
	</section>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import { orderConsultant } from "~/api/api";
import { PayBox } from "~/hooks/pay/index";
import { useI18n } from "~/composables/useI18n";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultCover from "~/assets/images/default.png";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Array,
		default: () => [],
	},
	variant: {
		type: String,
		default: "default",
	},
});

const route = useRoute();
const isOrder = ref(false);
const isAreaTeacher = computed(() => props.variant === "area");
const items = computed(() => (Array.isArray(props.data) ? props.data.map(formatServiceItem) : []));

function bindServiceImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `service_${item.id}`, "mentor_service_card_impression", {
		service_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		position: index + 1,
	});
}

const toServiceDetail = async (data, index) => {
	tracking?.track?.("mentor_service_card_click", {
		service_id: String(data.id),
		price_bucket: tracking.getPriceBucket(data.final_price ?? data.price),
		action_type: "card",
		position: typeof index === "number" ? index + 1 : undefined,
	});
	await navigateTo(`/seer/teacher-service/${route.params.id}/${data.id}`);
};

const order = async (data, index) => {
	openLogin?.();
	if (isOrder.value) return;
	isOrder.value = true;

	try {
		const res = await orderConsultant({
			teacher_uid: route.params.id,
			service_id: data.id,
		});

		if (import.meta.client) {
			const { logEvent } = await import("~/utils/firebase");
			logEvent("site_masterpage_pay");
		}
		tracking?.track?.("mentor_service_card_click", {
			service_id: String(data.id),
			price_bucket: tracking.getPriceBucket(data.final_price ?? data.price),
			action_type: "order",
			position: typeof index === "number" ? index + 1 : undefined,
		});

		PayBox.alter({
			orderInfo: res.data,
			finalPrice: res.data.final_amount,
		});
	} catch (error) {
		console.error("Order failed:", error);
	} finally {
		isOrder.value = false;
	}
};

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});

function formatServiceItem(item) {
	return {
		...item,
		name: pickLocalized(item, "name"),
		detail: pickLocalizedDetail(item),
		cover: pickServiceImage(item),
	};
}

function pickLocalized(item, key) {
	const isEn = locale.value === "en";
	return (isEn ? item?.[`${key}_en`] : item?.[key]) || item?.[key] || "";
}

function pickLocalizedDetail(item) {
	if (locale.value === "en") {
		return (
			item?.detail_en ||
			item?.description_en ||
			item?.intro_en ||
			item?.detail ||
			item?.description ||
			item?.intro ||
			""
		);
	}
	return item?.detail || item?.description || item?.intro || "";
}

function pickServiceImage(item) {
	const candidates =
		locale.value === "en"
			? [item?.image, item?.background_image, item?.image_jt]
			: [item?.image_jt, item?.image, item?.background_image];
	return candidates.map(getFirstImageUrl).find(Boolean) || "";
}

function getFirstImageUrl(value) {
	if (Array.isArray(value)) {
		return value.map(getFirstImageUrl).find(Boolean) || "";
	}
	if (typeof value !== "string") return "";
	return value
		.split(",")
		.map(item => item.trim())
		.find(Boolean);
}
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.section-title {
	margin: 0 0 24px;
	color: #333333;
	font-size: 24px;
	font-weight: 500;
	line-height: 1.3;
}

.consult-service {
	font-family: "Source Han Serif CN", "Source Han Serif CN";

	&__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 18px;
		margin: 0;
		padding: 0;
		list-style: none;
	}
}

.consult-service-card {
	display: flex;
	flex-direction: column;
	min-height: 214px;
	padding: 20px;
	border-radius: 8px;
	background: #f7f7f7;
	cursor: pointer;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
	}

	&__image-wrap {
		display: none;
	}

	&__image {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__content {
		display: flex;
		flex: 1;
		min-width: 0;
		flex-direction: column;
	}

	&__name {
		margin: 0;
		color: #333333;
		font-size: 18px;
		font-weight: 500;
		line-height: 1.3;
		@include line-clamp(1);
	}

	&__detail {
		flex: 1;
		margin: 12px 0 16px;
		color: #585858;
		font-size: 14px;
		line-height: 1.7;
		@include line-clamp(4);
	}

	&__original {
		margin: 0 0 8px;
		color: #999999;
		font-size: 14px;
		line-height: 1.4;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-top: auto;
	}

	&__price {
		display: flex;
		align-items: center;
		min-width: 0;
		color: #de2828;

		img {
			flex: 0 0 auto;
			width: 20px;
			height: 20px;
			margin-right: 4px;
		}

		span {
			font-family: "Source Han Serif CN", "Source Han Serif CN";
			font-size: 24px;
			font-weight: 700;
			line-height: 1;
		}

		em {
			margin-left: 6px;
			padding: 2px 6px;
			border-radius: 999px;
			background: #ffe4e2;
			color: #c62000;
			font-size: 12px;
			font-style: normal;
			line-height: 1.4;
			white-space: nowrap;
		}
	}

	&__btn {
		flex: 0 0 auto;
		min-width: 86px;
		height: 30px;
		padding: 0 12px;
		border: 0;
		border-radius: 4px;
		background: #333333;
		color: #ffffff;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
		white-space: nowrap;

		&:hover {
			opacity: 0.7;
		}
	}

	&__btn--icon {
		position: relative;
		width: 22px;
		min-width: 22px;
		height: 22px;
		padding: 0;
		border-radius: 50%;
	}

	&__plus {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 10px;
		height: 10px;
		transform: translate(-50%, -50%);

		&::before,
		&::after {
			content: "";
			position: absolute;
			left: 50%;
			top: 50%;
			background: #ffffff;
			border-radius: 1px;
			transform: translate(-50%, -50%);
		}

		&::before {
			width: 10px;
			height: 2px;
		}

		&::after {
			width: 2px;
			height: 10px;
		}
	}
}

.consult-service--area {
	.consult-service__grid {
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 24px;
	}

	.consult-service-card {
		min-height: 0;
		padding: 0;
		border: 1px solid #eeeeee;
		background: #ffffff;
		overflow: hidden;
	}

	.consult-service-card__image-wrap {
		display: block;
		aspect-ratio: 1;
		background: #f3f3f3;
		overflow: hidden;
	}

	.consult-service-card__content {
		padding: 12px 12px 14px;
	}

	.consult-service-card__name {
		font-size: 16px;
		font-weight: 500 !important;
	}

	.consult-service-card__detail {
		margin: 8px 0 12px;
		font-size: 12px;
		line-height: 1.55;
		@include line-clamp(2);
	}

	.consult-service-card__price {
		img {
			width: 16px;
			height: 16px;
		}

		span {
			font-size: 18px;
		}
	}

	.consult-service-card__btn {
		width: 22px;
		min-width: 22px;
		height: 22px;
		padding: 0;
	}
}

@media (max-width: 1024px) {
	.consult-service {
		&__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	.consult-service--area {
		.consult-service__grid {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}
}

@media (max-width: 640px) {
	.consult-service {
		&__grid {
			grid-template-columns: 1fr;
		}
	}

	.consult-service--area {
		.consult-service__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
}

@media (max-width: 420px) {
	.consult-service--area {
		.consult-service__grid {
			grid-template-columns: 1fr;
		}
	}
}
</style>
